import type { TableMenuItem } from '@/components'
import { TableMenu } from '@/components'
import {
  DELETE_ISSUE_MUTATION,
  TOGGLE_ISSUE_ARCHIVED_MUTATION,
  TOGGLE_ISSUE_CLOSED_MUTATION,
} from '@/modules/issues/mutations'
import { useConfirm, useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation } from '@apollo/client'
import { Loader, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useNavigate } from 'react-router'
import type { CloseIssueForm } from '../../types'
import type {
  IssueNodeForAllIssues,
  ToggleIssueArchivedMutation,
  ToggleIssueArchivedMutationVariables,
  ToggleIssueClosedMutation,
  ToggleIssueClosedMutationVariables,
} from '../../types.graphl'
import { CloseIssuePrompt } from './CloseIssueModal'

interface Props {
  issue: IssueNodeForAllIssues
}

/**
 * The row actions for one issue. It owns its own mutations so the table column
 * definitions stay free of per-render callbacks — Tanstack Table needs those
 * definitions stable.
 */
export function IssueRowMenu({ issue }: Props) {
  const translations = useTranslate({
    editIssue: 'issues.edit-issue',
    closeIssue: 'issues.close-issue',
    reopenIssue: 'issues.reopen-issue',
    archiveIssue: 'issues.archive-issue',
    restoreIssue: 'issues.restore-issue',
    deleteIssue: 'issues.delete-issue',
    deleteIssueConfirm: 'issues.delete-issue-confirm',
  })

  const navigate = useNavigate()
  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()
  const onErrorAuto = useOnErrorAuto()

  const mutationOptions = {
    awaitRefetchQueries: true,
    refetchQueries: ['Sidebar', 'AllIssues'],
    onError: onErrorAuto(),
  }

  const [toggleClosed, { loading: closeLoading }] = useMutation<
    ToggleIssueClosedMutation,
    ToggleIssueClosedMutationVariables
  >(TOGGLE_ISSUE_CLOSED_MUTATION, mutationOptions)
  const [toggleArchived, { loading: archiveLoading }] = useMutation<
    ToggleIssueArchivedMutation,
    ToggleIssueArchivedMutationVariables
  >(TOGGLE_ISSUE_ARCHIVED_MUTATION, mutationOptions)
  const [deleteIssue, { loading: deleteLoading }] = useMutation(
    DELETE_ISSUE_MUTATION,
    mutationOptions
  )

  const canEdit = hasPermissions(PERMISSIONS.issues.change.issue, true)
  const canDelete = hasPermissions(PERMISSIONS.issues.delete.issue, true)

  async function handleCloseIssue() {
    if (issue.closed) {
      await toggleClosed({
        variables: { id: issue.id, closed: false },
      })
      return
    }

    const { data } = await addPrompt<CloseIssueForm | null>(resolve => (
      <CloseIssuePrompt
        initialData={{
          solution: issue.solution ?? '',
          actualCost: issue.actualCost ?? 0,
        }}
        onResolve={resolve}
      />
    ))
    if (data === null) return

    await toggleClosed({
      variables: {
        id: issue.id,
        closed: true,
        solution: data?.solution,
        actualCost: data?.actualCost ?? undefined,
      },
    })
  }

  async function handleArchiveIssue() {
    await toggleArchived({
      variables: { id: issue.id, archived: !issue.archived },
    })
  }

  async function handleDeleteIssue() {
    const { data } = await confirm(
      translations.deleteIssueConfirm,
      translations.deleteIssue
    )
    if (!data) return

    await deleteIssue({ variables: { id: issue.id } })
  }

  const items: TableMenuItem[] = []

  if (canEdit) {
    if (!issue.closed && !issue.archived)
      items.push({
        id: 'edit',
        type: 'regular',
        label: translations.editIssue,
        icon: 'edit',
        onClick: () =>
          void navigate(`/issues/${issue.id}`, {
            state: { edit: true },
          }),
      })

    items.push({
      id: 'close',
      type: 'regular',
      label: !issue.closed ? translations.closeIssue : translations.reopenIssue,
      icon: !issue.closed ? 'check' : 'rotate-left',
      onClick: () => void handleCloseIssue(),
    })
    items.push({
      id: 'archive',
      type: 'regular',
      label: !issue.archived
        ? translations.archiveIssue
        : translations.restoreIssue,
      icon: !issue.archived ? 'box-archive' : 'arrow-up-from-bracket',
      onClick: () => void handleArchiveIssue(),
    })
  }

  if (canDelete) {
    items.push({
      id: 'delete',
      type: 'regular',
      label: translations.deleteIssue,
      icon: 'trash',
      onClick: () => void handleDeleteIssue(),
    })
  }

  if (items.length === 0) return null

  if (closeLoading || archiveLoading || deleteLoading)
    return <Loader.Spinner size={18} thickness={2} />

  return <TableMenu items={items} />
}
